import React, { useState, useEffect } from 'react';
import { BookOpen, Calendar } from 'lucide-react';
import { GlossaryTerm } from '../../types/glossary';
import { glossaryService } from '../../services/glossaryService';

/**
 * Displays a featured term of the day with enhanced styling
 */
export const TermOfTheDay: React.FC = () => {
  const [term, setTerm] = useState<GlossaryTerm | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTermOfTheDay = async () => {
      try {
        setLoading(true);
        const termOfDay = await glossaryService.getTermOfTheDay();
        setTerm(termOfDay);
      } catch (error) {
        console.error('Error fetching term of the day:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTermOfTheDay();
  }, []);

  if (loading) {
    return (
      <div className="bg-gradient-to-r from-blue-500/80 to-indigo-400/80 rounded-lg p-4 animate-pulse">
        <div className="flex items-center mb-4">
          <div className="bg-indigo-500/50 rounded-full p-2 mr-3">
            <BookOpen className="h-6 w-6 text-white" />
          </div>
          <h2 className="text-xl font-bold text-white">Term of the Day</h2>
        </div>
        <div className="h-4 bg-indigo-500/30 rounded w-3/4 mb-3"></div>
        <div className="h-4 bg-indigo-500/30 rounded w-full mb-3"></div>
        <div className="h-4 bg-indigo-500/30 rounded w-5/6"></div>
      </div>
    );
  }

  if (!term) {
    return (
      <div className="bg-gradient-to-r from-blue-500/80 to-indigo-400/80 rounded-lg p-4">
        <div className="flex items-center mb-4">
          <div className="bg-indigo-500/50 rounded-full p-2 mr-3">
            <BookOpen className="h-6 w-6 text-white" />
          </div>
          <h2 className="text-xl font-bold text-white">Term of the Day</h2>
        </div>
        <p className="text-indigo-200">No terms available at the moment.</p>
      </div>
    );
  }

  // Format the current date
  const today = new Date();
  const formattedDate = today.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });

  return (
    <div className="bg-gradient-to-r from-blue-500/80 to-indigo-400/80 rounded-lg p-4 shadow-lg relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-blue-300 rounded-full opacity-20 -mr-32 -mt-32"></div>
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-indigo-300 rounded-full opacity-20 -ml-24 -mb-24"></div>
      
      <div className="flex items-center mb-1">
        <div className="bg-indigo-500/50 rounded-full p-1.5 mr-2">
          <BookOpen className="h-5 w-5 text-white" />
        </div>
        <h2 className="text-lg font-bold text-white">Term of the Day</h2>
      </div>
      
      <h3 className="text-xl font-bold text-white mb-2">{term.term}</h3>
      
      <div className="text-indigo-100 text-sm">
        <div dangerouslySetInnerHTML={{ __html: term.content.replace(/<\/p><p>/g, '</p><br><p>') }} />
      </div>
    </div>
  );
};